import React, { useMemo } from "react";
import { Navigate, type ToolbarProps, Views } from "react-big-calendar";
import {
  addDays,
  isSameMonth,
  isSameWeek,
  isWithinInterval,
  startOfDay,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button, RadioButton, Select } from "@/components/atoms";
import { useIsMobileViewport } from "@/hooks";
import { THREE_DAY_VIEW } from "./threeDayView.constants";
import type { MappedCalendarEvent } from "./availabilityCalendar.types";
import "./AvailabilityCalendarToolbar.scss";

const NAV_OPTIONS = {
  prev: "Anterior",
  today: "Hoy",
  next: "Siguiente",
} as const;

const TODAY_OPTION = { today: NAV_OPTIONS.today } as const;

const DESKTOP_VIEW_OPTIONS = {
  month: "Mensual",
  week: "Semanal",
} as const;

const MOBILE_VIEW_OPTIONS = {
  month: "Mensual",
  [THREE_DAY_VIEW]: "3 días",
} as const;

const isThreeDayCurrentPeriod = (anchor: Date): boolean => {
  const start = startOfDay(anchor);
  const end = addDays(start, 2);

  return isWithinInterval(startOfDay(new Date()), { start, end });
};

/**
 * Custom toolbar for AvailabilityCalendar using design-system RadioButton controls
 */
const AvailabilityCalendarToolbar: React.FC<
  ToolbarProps<MappedCalendarEvent>
> = ({
  label,
  view,
  date,
  onNavigate,
  onView,
}) => {
  const isMobile = useIsMobileViewport();
  const viewOptions = isMobile ? MOBILE_VIEW_OPTIONS : DESKTOP_VIEW_OPTIONS;
  const normalizedView =
    view in viewOptions ? view : (Views.MONTH as ToolbarProps["view"]);

  const isCurrentPeriod = useMemo(() => {
    if (normalizedView === Views.WEEK) {
      return isSameWeek(date, new Date(), { weekStartsOn: 1 });
    }

    if (normalizedView === (THREE_DAY_VIEW as ToolbarProps["view"])) {
      return isThreeDayCurrentPeriod(date);
    }

    return isSameMonth(date, new Date());
  }, [date, normalizedView]);

  const handleViewChange = (value: string) => {
    onView(value as ToolbarProps["view"]);
  };

  return (
    <div className="availability-calendar-toolbar">
      <div className="availability-calendar-toolbar__section availability-calendar-toolbar__section--left">
        <div className="availability-calendar-toolbar__nav">
          <Button
            variant="secondary"
            density="compact"
            className="availability-calendar-toolbar__breakpoint--desktop"
            onClick={() => onNavigate(Navigate.PREVIOUS)}
          >
            {NAV_OPTIONS.prev}
          </Button>
          <Button
            variant="secondary"
            density="compact"
            className="availability-calendar-toolbar__breakpoint--mobile"
            onClick={() => onNavigate(Navigate.PREVIOUS)}
          >
            <ChevronLeft size={18} />
          </Button>
          <RadioButton
            options={TODAY_OPTION}
            value={isCurrentPeriod ? "today" : ""}
            onChange={(value) => {
              if (value === "today") {
                onNavigate(Navigate.TODAY);
              }
            }}
            variant="secondary"
            density="compact"
          />
          <Button
            variant="secondary"
            density="compact"
            className="availability-calendar-toolbar__breakpoint--desktop"
            onClick={() => onNavigate(Navigate.NEXT)}
          >
            {NAV_OPTIONS.next}
          </Button>
          <Button
            variant="secondary"
            density="compact"
            className="availability-calendar-toolbar__breakpoint--mobile"
            onClick={() => onNavigate(Navigate.NEXT)}
          >
            <ChevronRight size={18} />
          </Button>
        </div>
      </div>

      <div className="availability-calendar-toolbar__section availability-calendar-toolbar__section--center">
        <p className="availability-calendar-toolbar__label">{label}</p>
      </div>

      <div className="availability-calendar-toolbar__section availability-calendar-toolbar__section--right">
        <div className="availability-calendar-toolbar__breakpoint--desktop">
          <RadioButton
            options={viewOptions}
            value={normalizedView}
            onChange={handleViewChange}
            variant="secondary"
            density="compact"
          />
        </div>
        <div className="availability-calendar-toolbar__breakpoint--mobile availability-calendar-toolbar__breakpoint--mobile-full">
          <Select
            options={viewOptions}
            value={normalizedView}
            onChange={handleViewChange}
            density="compact"
          />
        </div>
      </div>
    </div>
  );
};

export default AvailabilityCalendarToolbar;
